import AWS, { AWSError, S3 } from 'aws-sdk';
import { GetObjectOutput, GetObjectRequest } from 'aws-sdk/clients/s3';
import { PromiseResult } from 'aws-sdk/lib/request';
import { Toast } from 'primereact/toast';

export default abstract class AWSService {
    private static AWS = AWS;
    private static S3: S3;
    private static S3publicKey: string | undefined;
    private static S3privateKey: string | undefined;
    private static readonly bucket: string = 'sharp-it-cvs';
    private static readonly region: string = 'eu-central-1';

    static {
        this.S3publicKey = process.env.REACT_APP_AWS_S3_PUBLIC_KEY;
        if (this.S3publicKey === undefined) {
            throw new Error("REACT_APP_AWS_S3_PUBLIC_KEY environment variable is not found. Add .env file in the root folder of the project and assign value to this variable.");
        }

        this.S3privateKey = process.env.REACT_APP_AWS_S3_PRIVATE_KEY;
        if (this.S3privateKey === undefined) {
            throw new Error("REACT_APP_AWS_S3_PRIVATE_KEY environment variable is not found. Add .env file in the root folder of the project and assign value to this variable.");
        }

        AWSService.AWS.config.update({
            accessKeyId: this.S3publicKey,
            secretAccessKey: this.S3privateKey,
            region: AWSService.region,
        });

        AWSService.S3 = new AWSService.AWS.S3();
    }

    public static uploadFile(file: File, toast?: Toast): Promise<string> {
        let downloadURL: string = '';

        if (file === null || file === undefined) {
            throw new Error('File can not be null or undefined.');
        }

        const fileNameParts: Array<string> = file.name.split('.');
        const fileExtention = fileNameParts[fileNameParts.length - 1];
        // Insert timestamp before file extension.
        // Expected results: 
        //      - filename.doc => filename_{timestamp}.doc
        //      - file.name.doc => file.name_{timestamp}.doc
        const fileName = `${fileNameParts.slice(0, -1).join('.')}_${Date.now()}.${fileExtention}`;

        const AWSParams = {
            Bucket: AWSService.bucket, // Bucket name
            Key: fileName, // File name in S3
            Body: file, // File object
            ContentType: file.type, // File type (e.g., 'image/png')
        };

        return new Promise<string>((resolve, reject) => {
            AWSService.S3.upload(AWSParams, (err: Error, data: S3.ManagedUpload.SendData): void  => {
                if (err) {
                    toast?.show({ 
                        severity: 'error', 
                        summary: 'Unexpected error occurred.', 
                        detail: 'File is not uploaded. Please use the contact form to report this problem.' 
                    });
                    console.error('Error uploading file:', err);
                    reject('File upload failed.');
                } else {
                    downloadURL = data.Location;
                    resolve(downloadURL);
                }
            });
        })
    }

    public static async downloadFile(fileKey: string, toast: Toast): Promise<Blob | undefined> {
        try {
            const params: GetObjectRequest = {
                Bucket: this.bucket,
                Key: fileKey,
            }
            // Fetch the file from S3
            const data: GetObjectOutput  = await AWSService.S3.getObject(params).promise();

            if (data.Body !== undefined) {
                // Create a Blob from the file data
                const blob: Blob = new Blob([data.Body as BlobPart], { type: data.ContentType });
                return blob;
            } else {
                this.showDownloadingError(toast);
                return undefined;

            }
        } catch (error) {
            this.showDownloadingError(toast);
            return undefined;
        }
    }

    private static showDownloadingError(toast: Toast): void {
        toast.show({ 
            severity: 'error', 
            summary: 'Unexpected error occured.', 
            detail: 'Couldn\'t fetch file from AWS S3 bucket.'
        });
    }
}